import React from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { TOKENS } from "./tokens";
import Monogram from "./Monogram";

const variantStyles = {
  danger: {
    bg: TOKENS.dangerBg,
    ink: TOKENS.dangerInk,
    icon: <DeleteOutlineIcon />,
    submitBg: TOKENS.danger,
    submitHover: TOKENS.dangerHover,
  },
  warning: {
    bg: TOKENS.warningBg,
    ink: TOKENS.warningInk,
    icon: <WarningAmberIcon />,
    submitBg: TOKENS.warning,
    submitHover: "#D49A19",
  },
  success: {
    bg: TOKENS.successBg,
    ink: TOKENS.successInk,
    icon: <CheckCircleOutlineIcon />,
    submitBg: TOKENS.success,
    submitHover: "#179B57",
  },
};

const ConfirmModal = ({
  open,
  onClose,
  onConfirm,
  variant = "danger",
  title,
  body,
  items,
  submitLabel = "Confirm",
  cancelLabel = "Cancel",
  loading = false,
}) => {
  const v = variantStyles[variant] || variantStyles.danger;

  return (
    <Dialog
      open={!!open}
      onClose={loading ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: `${TOKENS.radiusM}px`,
          boxShadow: TOKENS.shadowL,
          maxWidth: 480,
          width: "100%",
          background: TOKENS.bgSurface,
        },
      }}
    >
      <Box sx={{ p: "20px 22px 18px" }}>
        <Box sx={{ display: "flex", gap: 1.75 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: `${TOKENS.radiusXs}px`,
              background: v.bg,
              color: v.ink,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {v.icon}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 17,
                fontWeight: 700,
                color: TOKENS.fg1,
                letterSpacing: "-0.01em",
              }}
            >
              {title}
            </Typography>
            {body ? (
              <Typography sx={{ fontSize: 13.5, color: TOKENS.fg2, mt: 0.75, lineHeight: 1.5 }}>
                {body}
              </Typography>
            ) : null}
          </Box>
        </Box>

        {Array.isArray(items) && items.length > 0 ? (
          <Box
            sx={{
              mt: 2,
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              maxHeight: 168,
              overflowY: "auto",
              background: TOKENS.bgSunken,
            }}
          >
            {items.map((it, idx) => (
              <Box
                key={it.id || idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  px: 1.5,
                  py: 1,
                  borderBottom:
                    idx === items.length - 1 ? "none" : `1px solid ${TOKENS.borderSubtle}`,
                }}
              >
                <Monogram name={it.title || ""} size={28} radius={8} />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: TOKENS.fg1,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {it.title}
                  </Typography>
                  {it.id ? (
                    <Typography
                      sx={{
                        fontSize: 11,
                        color: TOKENS.fg3,
                        fontFamily:
                          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                      }}
                    >
                      {it.id}
                    </Typography>
                  ) : null}
                </Box>
              </Box>
            ))}
          </Box>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2.5 }}>
          <Button
            onClick={onClose}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: TOKENS.fg1,
              background: "transparent",
              border: `1px solid ${TOKENS.border}`,
              borderRadius: `${TOKENS.radiusXs}px`,
              px: 2,
              "&:hover": { background: TOKENS.bgSunken },
            }}
          >
            {cancelLabel}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={loading}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              color: "#fff",
              background: v.submitBg,
              borderRadius: `${TOKENS.radiusXs}px`,
              px: 2,
              minWidth: 96,
              "&:hover": { background: v.submitHover },
              "&.Mui-disabled": { color: "#fff", opacity: 0.7 },
            }}
          >
            {loading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : submitLabel}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ConfirmModal;
